import { formatDay, personName } from "./format";
import type { QrChange, QrCodeSummary, QrPlacement, QrScanRow, QrStatus, ReleaseStatus, ScanGrain } from "./qr-api";

// Formatting and zero-filling only. Every count is the Worker's (I-4).

/** The printed address as a person reads it: no scheme, and a family reads `…/q/lot/<batch>`. */
export function printedAddress(printedUrl: string): string {
  const bare = printedUrl.replace(/^https?:\/\//, "");
  return bare.endsWith("/") ? `${bare}<batch>` : bare;
}

/** Host and path of a destination, cut to `max` characters. The full URL goes in the hover title. */
export function targetLabel(url: string, max = 44): string {
  let text = url.replace(/^https?:\/\//, "");
  try {
    const parsed = new URL(url);
    text = `${parsed.host}${parsed.pathname === "/" ? "" : parsed.pathname}${parsed.search ? "?…" : ""}`;
  } catch {
    // Not a URL: show it as it is.
  }
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/** `batch_report` -> "Batch report". */
export function placementLabel(placement: QrPlacement | string): string {
  const words = placement.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Every placement of a code, joined with ` · `. */
export function placementsLabel(placements: readonly (QrPlacement | string)[]): string {
  return placements.length === 0 ? "–" : placements.map(placementLabel).join(" · ");
}

/** A code needs at least one placement (transparency spec P2). */
export function placementsOk(placements: readonly string[]): boolean {
  return placements.length > 0;
}

/** The same set, whatever order it was ticked in. */
export function samePlacements(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");
}

/** The part of a name before ` · ` (transparency spec N): the funnel the code sends people to. */
export function funnelOf(label: string): string {
  const cut = label.indexOf(" · ");
  return cut === -1 ? label : label.slice(0, cut);
}

export function releaseStatusLabel(status: ReleaseStatus | string): string {
  switch (status) {
    case "draft":
      return "○ Draft";
    case "published":
      return "● Published";
    case "rolled_back":
      return "↺ Rolled back";
    case "discarded":
      return "– Discarded";
    default:
      return status;
  }
}

export function releaseStatusClass(status: ReleaseStatus | string): string {
  switch (status) {
    case "published":
      return "bg-ks-pass/10 text-ks-pass";
    case "rolled_back":
      return "bg-ks-warn/10 text-ks-warn";
    case "discarded":
      return "bg-muted text-muted-foreground";
    default:
      return "border border-border text-ks-ink";
  }
}

/** Publish needs the release name typed exactly, and at least one change in it. */
export function publishReady(input: { name: string; typed: string; changeCount: number }): boolean {
  return input.changeCount > 0 && input.typed === input.name;
}

/** A person and a day: "Damien, 1 Oct 2026". */
export function whoWhen(by: string | null, at: string | null): string {
  if (!at) return "–";
  return `${by ? `${personName(by)}, ` : ""}${formatDay(at.slice(0, 10))}`;
}

/** Where the scan rows and the code list link to: the QR Codes tab, with that code open. */
export function codeDetailSearch(previous: Record<string, unknown>, id: string): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(previous)) if (key.startsWith("f_")) next[key] = value;
  next.f_q_code = id;
  delete next.f_q_view;
  delete next.f_q_release;
  return next;
}

/** Glyph + word, never colour alone. */
export function statusLabel(status: QrStatus | string): string {
  switch (status) {
    case "live":
      return "● Live";
    case "paused":
      return "◐ Paused";
    case "retired":
      return "– Retired";
    case "draft":
      return "○ Draft";
    default:
      return status;
  }
}

/** Status tokens (`ks-pass` / `ks-warn`), not the KPI band palette. */
export function statusClass(status: QrStatus | string): string {
  switch (status) {
    case "live":
      return "bg-ks-pass/10 text-ks-pass";
    case "paused":
      return "bg-ks-warn/10 text-ks-warn";
    case "retired":
      return "bg-muted text-muted-foreground";
    default:
      return "border border-border text-ks-ink";
  }
}

/** The status chip value that means "Live + Paused" (the default) and the one that means every status. */
export const STATUS_DEFAULT = "";
export const STATUS_ALL = "all";

/** A URL value as a list: one string, an array, or nothing. */
export function asList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter((item) => item !== "");
  return typeof value === "string" && value !== "" ? [value] : [];
}

/** Placements are "any of": a code shows if it sits in at least one chosen place. */
export function filterCodes(codes: QrCodeSummary[], places: string[], status: string): QrCodeSummary[] {
  return codes.filter((code) => {
    if (places.length > 0 && !code.placements.some((place) => places.includes(place))) return false;
    if (status === STATUS_ALL) return true;
    if (status === STATUS_DEFAULT) return code.status === "live" || code.status === "paused";
    return code.status === status;
  });
}

/** The first `limit` rows as they came (the Worker sorts by scans), and the rest gathered as "Other". */
export function topWithOther(rows: { label: string; scans: number }[], limit = 10): { label: string; scans: number }[] {
  if (rows.length <= limit) return rows;
  const rest = rows.slice(limit).reduce((sum, row) => sum + row.scans, 0);
  return [...rows.slice(0, limit), { label: "Other", scans: rest }];
}

/** Sums several codes' country or device rows into one list, busiest first (the "All codes" breakdown). */
export function mergeBreakdown(lists: { label: string; scans: number }[][]): { label: string; scans: number }[] {
  const totals = new Map<string, number>();
  for (const list of lists) for (const row of list) totals.set(row.label, (totals.get(row.label) ?? 0) + row.scans);
  return [...totals.entries()]
    .map(([label, scans]) => ({ label, scans }))
    .sort((a, b) => b.scans - a.scans || a.label.localeCompare(b.label));
}

let regionNames: Intl.DisplayNames | null | undefined;

/** `GB` -> `United Kingdom`; `XX` (the Worker's "no country") -> `Unknown`. */
export function countryName(code: string): string {
  if (code === "XX" || code === "") return "Unknown";
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    } catch {
      regionNames = null;
    }
  }
  try {
    return regionNames?.of(code) ?? code;
  } catch {
    return code;
  }
}

export function deviceLabel(device: string): string {
  switch (device) {
    case "ios":
      return "iPhone or iPad";
    case "android":
      return "Android";
    case "desktop":
      return "Computer";
    default:
      return "Other";
  }
}

/**
 * The address a scan lands on, written out: the destination plus the tagging
 * rule the Worker applies (`utm_source=qr`, `utm_campaign=<id>`, only where
 * absent). Query parameters on the printed address are merged by the Worker at
 * scan time, so they cannot be shown here.
 */
export function redirectPreview(target: string, tagDestination: boolean, id: string): string | null {
  let url: URL;
  try {
    url = new URL(target);
  } catch {
    return null;
  }
  if (url.protocol !== "https:") return null;
  if (tagDestination) {
    if (!url.searchParams.has("utm_source")) url.searchParams.set("utm_source", "qr");
    if (!url.searchParams.has("utm_campaign")) url.searchParams.set("utm_campaign", id);
  }
  // A family target's `{key}` placeholder is legal and stays visible; the URL API would escape it.
  return url.toString().replace(/%7Bkey%7D/gi, "{key}");
}

export function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Whether Change destination may be pressed: a real https address that differs
 * from the current one, and, for a LIVE code, the code id typed exactly.
 */
export function destinationReady(input: { next: string; current: string; live: boolean; typed: string; id: string }): boolean {
  if (!isHttpsUrl(input.next) || input.next === input.current) return false;
  return !input.live || input.typed === input.id;
}

const FIELD_LABELS: Record<string, string> = {
  label: "Name",
  placements: "Placements",
  product: "Product",
  campaign: "Campaign",
  print_run: "Print run",
  target_url: "Goes to",
  fallback_url: "Fallback address",
  tag_destination: "Tagging",
  status: "Status",
  notes: "Notes",
  match_kind: "Match",
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

function valueLabel(field: string, value: string | null): string {
  if (value === null || value === "") return "empty";
  if (field === "status") return value.charAt(0).toUpperCase() + value.slice(1);
  if (field === "placements") {
    try {
      const list: unknown = JSON.parse(value);
      if (Array.isArray(list)) return placementsLabel(list.map(String));
    } catch {
      // Not JSON: show it as it is.
    }
  }
  if (field === "tag_destination") return value === "1" || value === "true" ? "on" : "off";
  return value;
}

/** One history entry in words: "Damien, 1 Oct 2026: Goes to: https://old → https://new". */
export function changeLine(change: QrChange): string {
  const when = formatDay(change.changed_at.slice(0, 10));
  return `${personName(change.changed_by)}, ${when}: ${fieldLabel(change.field)}: ${valueLabel(change.field, change.old_value)} → ${valueLabel(change.field, change.new_value)}`;
}

// ---- QR Scans tab -------------------------------------------------------
// The Worker rolls days up into weeks and months and owns every unique-scanner
// figure (transparency spec S). What follows only pivots rows it already sent:
// it never changes the grain and never derives a new measure.

/** Every period from `first` to `last` at the grain the Worker used, so a quiet period draws as a zero. */
export function periodsBetween(first: string, last: string, grain: ScanGrain): string[] {
  const out: string[] = [];
  const cursor = new Date(`${first}T00:00:00Z`);
  const end = new Date(`${last}T00:00:00Z`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime())) return out;
  while (cursor <= end && out.length < 1000) {
    out.push(cursor.toISOString().slice(0, 10));
    if (grain === "month") cursor.setUTCMonth(cursor.getUTCMonth() + 1);
    else cursor.setUTCDate(cursor.getUTCDate() + (grain === "week" ? 7 : 1));
  }
  return out;
}

export type ScanPeriodRow = { period: string; scans: number; unique_scanners: number };

/** One row per period for the codes the Worker returned, quiet periods as zero. */
export function scansByPeriod(rows: QrScanRow[], grain: ScanGrain): ScanPeriodRow[] {
  if (rows.length === 0) return [];
  const periods = rows.map((row) => row.period).sort();
  const totals = new Map<string, ScanPeriodRow>();
  for (const row of rows) {
    const entry = totals.get(row.period) ?? { period: row.period, scans: 0, unique_scanners: 0 };
    entry.scans += row.scans;
    entry.unique_scanners += row.unique_scanners;
    totals.set(row.period, entry);
  }
  return periodsBetween(periods[0], periods[periods.length - 1], grain).map(
    (period) => totals.get(period) ?? { period, scans: 0, unique_scanners: 0 },
  );
}

export type ScanCodeTotal = { code_id: string; scans: number; unique_scanners: number };

/** Per-code figures over the window, most scans first. */
export function scansByCode(rows: QrScanRow[]): ScanCodeTotal[] {
  const totals = new Map<string, ScanCodeTotal>();
  for (const row of rows) {
    const entry = totals.get(row.code_id) ?? { code_id: row.code_id, scans: 0, unique_scanners: 0 };
    entry.scans += row.scans;
    entry.unique_scanners += row.unique_scanners;
    totals.set(row.code_id, entry);
  }
  return [...totals.values()].sort((a, b) => b.scans - a.scans || a.code_id.localeCompare(b.code_id));
}

export const SPLIT_LIMIT = 8;
export const OTHER_KEY = "other";

/** Scans per period with one column per code: the `limit` busiest codes, the rest gathered as `other`. */
export function scansSplit(
  rows: QrScanRow[],
  grain: ScanGrain,
  limit = SPLIT_LIMIT,
): { keys: string[]; hasOther: boolean; data: ({ period: string } & Record<string, number | string>)[] } {
  const ranked = scansByCode(rows).map((entry) => entry.code_id);
  const shown = ranked.slice(0, limit);
  const hasOther = ranked.length > limit;
  if (rows.length === 0) return { keys: shown, hasOther, data: [] };
  const periods = rows.map((row) => row.period).sort();
  const cells = new Map<string, Record<string, number>>();
  for (const row of rows) {
    const key = shown.includes(row.code_id) ? row.code_id : OTHER_KEY;
    const cell = cells.get(row.period) ?? {};
    cell[key] = (cell[key] ?? 0) + row.scans;
    cells.set(row.period, cell);
  }
  const data = periodsBetween(periods[0], periods[periods.length - 1], grain).map((period) => {
    const row: { period: string } & Record<string, number | string> = { period };
    for (const key of hasOther ? [...shown, OTHER_KEY] : shown) row[key] = cells.get(period)?.[key] ?? 0;
    return row;
  });
  return { keys: shown, hasOther, data };
}

/** A day is itself, a week reads "w/c 28 Sep 2026", a month "Sep 2026". */
export function periodLabel(period: string, grain: ScanGrain): string {
  if (grain === "day") return formatDay(period);
  if (grain === "week") return `w/c ${formatDay(period)}`;
  const date = new Date(`${period}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? period : date.toLocaleDateString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
}

export const SCAN_DAYS = ["7", "30", "90", "365"] as const;

export function parseScanDays(value: unknown): 7 | 30 | 90 | 365 {
  return value === "7" || value === "90" || value === "365" ? (Number(value) as 7 | 90 | 365) : 30;
}

export function parseGrain(value: unknown): ScanGrain {
  return value === "week" || value === "month" ? value : "day";
}
